import { useState } from 'react';

/**
 * "When did you take it?"
 *
 * The product's whole value is the ACTUAL time of a dose. If Richard takes his pill at
 * 7:00 and logs it at 7:45, recording 7:45 quietly corrupts the one thing the doctors
 * need — and Denise's rule ("write the actual time immediately") only holds when
 * writing it immediately was possible.
 *
 * Coarse buttons first, because subtracting twenty minutes is easier than operating a
 * spinner with a tremor. Typing an exact time is the escape hatch, not the default.
 */

export interface TimeChoice {
  /** Minutes before now. 0 means "just now". */
  minutesAgo: number;
  label: string;
}

const OFFSETS: TimeChoice[] = [
  { minutesAgo: 0, label: 'Just now' },
  { minutesAgo: 15, label: '15 minutes ago' },
  { minutesAgo: 30, label: '30 minutes ago' },
  { minutesAgo: 60, label: 'An hour ago' },
  { minutesAgo: 120, label: '2 hours ago' },
  { minutesAgo: 240, label: '4 hours ago' },
];

export function TimePicker({
  title,
  medName,
  busy,
  onChoose,
  onCancel,
}: {
  title: string;
  medName: string;
  busy: boolean;
  /** `at` is the chosen instant; `exact` says the person typed it rather than tapped an offset. */
  onChoose: (at: Date, exact: boolean) => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | undefined>();

  function chooseTyped(): void {
    const [h, m] = typed.split(':').map(Number);
    if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) {
      setError('Please choose a time.');
      return;
    }
    const at = new Date();
    at.setHours(h, m, 0, 0);

    // A dose cannot have been taken in the future. If the chosen clock time is later
    // than now it is almost certainly yesterday evening being logged this morning, so
    // roll it back a day rather than refusing and making the person work it out.
    if (at.getTime() > Date.now()) at.setDate(at.getDate() - 1);

    onChoose(at, true);
  }

  return (
    <main className="app">
      <h1>{title}</h1>
      <p className="muted">
        {medName} — when did you take it? The time is what matters, so it is worth
        getting close.
      </p>

      <div className="btn-row">
        {OFFSETS.map((choice) => (
          <button
            key={choice.minutesAgo}
            className="btn-quiet big"
            disabled={busy}
            onClick={() => {
              onChoose(new Date(Date.now() - choice.minutesAgo * 60_000), false);
            }}
          >
            {choice.label}
          </button>
        ))}
      </div>

      <h2>Or type the time</h2>
      <div className="stack">
        <input
          className="field"
          type="time"
          aria-label="Time the dose was taken"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setError(undefined);
          }}
        />
        <button
          className="btn-primary"
          disabled={busy || typed.length === 0}
          onClick={chooseTyped}
        >
          {busy ? 'Saving…' : 'Use this time'}
        </button>
      </div>
      {error !== undefined && <p className="error">{error}</p>}

      <div className="btn-row">
        <button className="btn-quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </main>
  );
}
